import React, { useRef } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationsCta } from '../../hooks/useAccommodationsCta';
import { AccommodationCombined, AccommodationSourceType } from '../Accommodations/Accommodation.types';
import { EventVenue } from '@shared/utils/eventPlace';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { PartnerHotelRedirect, PartnerHotelRedirectRef } from './PartnerHotelRedirect';
import { createSearchParamsFromAccommodationsPage } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

interface WrapPartnerHotelRedirectProps {
  locationInfo?: EventVenue;
  accommodation: AccommodationCombined;
  from?: AccommodationSourceType;
  onAccommodationsConsoleOpen?: () => void;
}

/**
 * HOC that provides redirect and selection logic to a child component.
 * @param WrappedComponent - The component to wrap and inject `onSelectAccommodation`
 */
export const wrapPartnerHotelRedirect = <P extends object>(
  WrappedComponent: React.ComponentType<P & { onSelectAccommodation: (event: React.MouseEvent<HTMLAnchorElement>) => void }>,
  source?: string,
) => {
  return (props: P & WrapPartnerHotelRedirectProps) => {
    const { accommodation, locationInfo, from, onAccommodationsConsoleOpen } = props;

    const partnerHotelRedirectRef = useRef<PartnerHotelRedirectRef>(null);

    const [accommodationsGuestRedirectExperiment, loadingExperiment] = useIsFeatureEnabled('accommodationsGuestRedirectExperiment');
    const accommodationsGuestRedirectExperimentEnabled = accommodationsGuestRedirectExperiment && !loadingExperiment;

    const { handleOnSelectAccommodation, isLinkEnabled, onSoldOutItemClick } = useAccommodationsCta({
      accommodation,
      locationInfo,
      from,
      searchParams: createSearchParamsFromAccommodationsPage('partner_hotel_redirect'),
      onSoldOutItemClickAction: onAccommodationsConsoleOpen,
    });

    const onSelectAccommodation = useEventCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
      handleOnSelectAccommodation(source);
      if (isLinkEnabled) {
        partnerHotelRedirectRef.current?.handleOnSelectAccommodation(event);
      } else {
        onSoldOutItemClick();
      }
    });

    return (
      <>
        <WrappedComponent
          {...props}
          onSelectAccommodation={onSelectAccommodation}
        />
        {accommodationsGuestRedirectExperimentEnabled && (
          <PartnerHotelRedirect
            ref={partnerHotelRedirectRef}
            id={accommodation.id}
            type={accommodation.type}
            displayName={accommodation.displayName}
            bookingUrl={accommodation.bookingUrl}
            onAccommodationsConsoleOpen={onAccommodationsConsoleOpen}
          />
        )}
      </>
    );
  };
};
